import { useCallback, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { EDIT_KEYS, changedKeys, convertLine, sumByCurrency } from "@/lib/lineMath";

const PAYLOAD_KEYS = ["product_id", "product_name", "variant_id", "variant_sku", "variant_attributes", "warehouse_id",
  "quantity", "unit_price", "currency", "tax_rate", "discount_percent", "discount_amount", "notes"];

const cleanApps = (apps) => (apps || []).map((a) => ({
  service_id: a.service_id || "", service_code: a.service_code || "", service_name: a.service_name || "",
  quantity: Number(a.quantity || 0), unit: a.unit || "adet", unit_price: Number(a.unit_price || 0),
  currency: a.currency || "TRY", tax_rate: Number(a.tax_rate || 0), send_to_production: Boolean(a.send_to_production),
  production_department_id: a.production_department_id || "", production_department_name: a.production_department_name || "",
}));

// Satır taslakları: sunucu satırları değişmez; düzenlemeler id → yama olarak tutulur, kayıt tek toplu istekte
export default function useLineDrafts(orderId) {
  const [orig, setOrig] = useState([]);
  const [updatedAt, setUpdatedAt] = useState("");
  const [drafts, setDrafts] = useState({});
  const [added, setAdded] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const cache = useRef(new Map());
  const savingRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, l] = await Promise.all([api.get(`/orders/${orderId}`), api.get(`/orders/${orderId}/lines`)]);
      setOrig(l.data || []);
      setUpdatedAt(o.data?.updated_at || "");
      setDrafts({}); setAdded([]); cache.current = new Map();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [orderId]);

  // Dosya ekleme gibi ticari olmayan değişikliklerden sonra: taslaklar korunur, yalnız sunucu durumu tazelenir
  const refresh = useCallback(async () => {
    try {
      const [o, l] = await Promise.all([api.get(`/orders/${orderId}`), api.get(`/orders/${orderId}/lines`)]);
      setOrig(l.data || []); setUpdatedAt(o.data?.updated_at || "");
    } catch (e) { toast.error(formatApiError(e)); }
  }, [orderId]);

  const origMap = useMemo(() => Object.fromEntries(orig.map((l) => [l.id, l])), [orig]);

  // Değişmeyen satırların nesne kimliği korunur → memo'lu satırlar yeniden çizilmez
  const rows = useMemo(() => {
    const out = [];
    const next = new Map();
    for (const l of [...orig, ...added]) {
      const d = drafts[l.id];
      const prev = cache.current.get(l.id);
      let r = prev && prev.base === l && prev.d === d ? prev : null;
      if (!r) {
        const row = d ? { ...l, ...d } : l;
        const keys = changedKeys(origMap[l.id], row);
        if (row._deleted) keys.push("deleted");
        r = { base: l, d, row, sig: keys.join(",") };
      }
      next.set(l.id, r);
      out.push(r);
    }
    cache.current = next;
    return out;
  }, [orig, added, drafts, origMap]);

  const dirtyRows = useMemo(() => rows.filter((r) => r.sig), [rows]);

  const patch = useCallback((id, p) => {
    setDrafts((ds) => ({ ...ds, [id]: { ...(ds[id] || {}), ...p } }));
  }, []);
  const bulkPatch = useCallback((ids, p) => {
    setDrafts((ds) => {
      const n = { ...ds };
      for (const id of ids) n[id] = { ...(n[id] || {}), ...p };
      return n;
    });
  }, []);
  const convert = useCallback((ids, to, fx, rowById) => {
    setDrafts((ds) => {
      const n = { ...ds };
      for (const id of ids) n[id] = { ...(n[id] || {}), ...convertLine(rowById[id], to, fx), _appsChanged: (rowById[id].applications || []).length > 0 || n[id]?._appsChanged };
      return n;
    });
  }, []);
  const addRow = useCallback((row) => {
    const id = `new-${Math.random().toString(36).slice(2, 10)}`;
    setAdded((a) => [...a, { ...row, id, attachments: [] }]);
    return id;
  }, []);
  const discard = useCallback(() => { setDrafts({}); setAdded([]); }, []);

  const summary = useCallback(() => {
    const counts = { rows: 0, unit_price: 0, quantity: 0, tax_rate: 0, discount: 0, currency: 0, added: 0, deleted: 0 };
    for (const { sig } of dirtyRows) {
      const k = sig.split(",");
      counts.rows += 1;
      if (k.includes("new")) { counts.added += 1; continue; }
      if (k.includes("deleted")) { counts.deleted += 1; continue; }
      for (const f of ["unit_price", "quantity", "tax_rate", "currency"]) if (k.includes(f)) counts[f] += 1;
      if (k.includes("discount_percent") || k.includes("discount_amount")) counts.discount += 1;
    }
    const before = sumByCurrency(orig);
    const after = sumByCurrency(rows.filter((r) => !r.row._deleted).map((r) => r.row));
    const curs = [...new Set([...Object.keys(before), ...Object.keys(after)])];
    return { counts, totals: curs.map((c) => ({ cur: c, before: before[c]?.total || 0, after: after[c]?.total || 0 })) };
  }, [dirtyRows, orig, rows]);

  const save = useCallback(async (changeSummary) => {
    if (savingRef.current) return false;
    savingRef.current = true; setSaving(true);
    try {
      const upserts = [], deletes = [];
      for (const { row, sig } of dirtyRows) {
        const isNew = String(row.id).startsWith("new-");
        if (row._deleted) { if (!isNew) deletes.push(row.id); continue; }
        const item = Object.fromEntries(PAYLOAD_KEYS.map((k) => [k, row[k] ?? (k === "variant_attributes" ? {} : "")]));
        item.quantity = Math.round(Number(row.quantity || 0));
        for (const k of ["unit_price", "tax_rate", "discount_percent", "discount_amount"]) item[k] = Number(row[k] || 0);
        item.currency = row.currency || "TRY";
        item.stock_qty = row.stock_qty ?? null;
        item.remaining_supply_method = row.stock_qty == null ? null : (row.remaining_supply_method || "");
        if (isNew || sig.includes("applications")) item.applications = cleanApps(row.applications);
        if (!isNew) {
          item.id = row.id;
          const o = origMap[row.id];
          item.expected = Object.fromEntries(EDIT_KEYS.map((k) => [k, k === "currency" ? (o.currency || "TRY") : Number(o[k] || 0)]));
        }
        upserts.push(item);
      }
      const { data } = await api.put(`/orders/${orderId}/lines/batch`, {
        expected_updated_at: updatedAt, change_summary: changeSummary || "", upserts, deletes,
      });
      setOrig(data.lines || []); setUpdatedAt(data.updated_at || ""); setDrafts({}); setAdded([]);
      toast.success(`${upserts.length + deletes.length} satır kaydedildi`);
      return true;
    } catch (e) {
      toast.error(formatApiError(e), { duration: 6000 });
      return false;
    } finally { savingRef.current = false; setSaving(false); }
  }, [dirtyRows, orderId, origMap, updatedAt]);

  return { rows, origMap, dirtyRows, loading, saving, load, refresh, patch, bulkPatch, convert, addRow, discard, summary, save };
}
